import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Hls from "hls.js";
import { Link } from "react-router-dom";
import { MonitorPlay, Square, Save, Upload, Radio, Zap, Loader2, Video, Twitch, Link2, Tv, AlertTriangle, ScreenShare, Sparkles, Lock } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LiveCoachPanel } from "@/components/live/LiveCoachPanel";
import { DesktopBanner } from "@/components/live/DesktopBanner";
import { useLiveCoach } from "@/context/LiveCoachContext";
import { useAuth } from "@/context/AuthContext";
import { createTicker } from "@/lib/ticker";
import { createControllerResearch } from "@/lib/controllerResearch";
import {
    appendPendingRecordingChunk,
    deleteRecoverableRecording,
    listRecoverableRecordings,
    loadRecoverableRecording,
    patchPendingRecording,
    startPendingRecording,
} from "@/lib/recordingStore";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const BUFFERS = [30, 60, 90, 120];
const EVENTS = ["Hotkey Trigger", "Kill", "Victory", "Top Placement", "Downed", "Unusual Engagement"];
const WEBM_CLUSTER_ID = [0x1f, 0x43, 0xb6, 0x75];
const SESSION_VIDEO_BITS_PER_SECOND = 1_400_000;
const SESSION_AUDIO_BITS_PER_SECOND = 96_000;
// A live-session recording is too valuable to abandon after one brief object-
// storage outage. This spans roughly two minutes and preserves idempotency.
const UPLOAD_RETRY_DELAYS_MS = [0, 1500, 4000, 8000, 15000, 30000, 45000];
const SESSION_UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;
const RECORDER_TIMESLICE_MS = 2000;
const LONG_TASK_DEGRADE_MS = 750;
const SLOW_ANALYSIS_DEGRADE_MS = 3500;
const LATENCY_GUARD_WINDOW_MS = 60_000;

function clipWindow(totalSeconds) {
    const total = Math.max(15, Number(totalSeconds) || 60);
    const after = Math.max(8, Math.min(20, Math.round(total / 3)));
    return { before: total - after, after, total };
}

function preferredRecorderMime() {
    // VP8 is substantially more portable than VP9 for raw-session fallback playback.
    // The server still normalizes finished recordings to H.264/AAC MP4.
    const candidates = ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm"];
    return candidates.find((mime) => MediaRecorder.isTypeSupported?.(mime)) || "";
}

function createSessionRecorder(stream, mimeType) {
    const options = {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: SESSION_VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: SESSION_AUDIO_BITS_PER_SECOND,
    };
    try {
        return new MediaRecorder(stream, options);
    } catch {
        return new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    }
}

function canvasIsEffectivelyBlack(canvas) {
    try {
        const context = canvas.getContext("2d", { willReadFrequently: true });
        const width = Math.min(32, canvas.width);
        const height = Math.min(18, canvas.height);
        if (!width || !height) return true;
        const probe = document.createElement("canvas");
        probe.width = width;
        probe.height = height;
        const probeContext = probe.getContext("2d", { willReadFrequently: true });
        probeContext.drawImage(canvas, 0, 0, width, height);
        const pixels = probeContext.getImageData(0, 0, width, height).data;
        let lit = 0;
        for (let i = 0; i < pixels.length; i += 4) {
            // Count pixels with enough luminance to distinguish a real dark game scene
            // from an all-black/transparent capture surface.
            if ((pixels[i] * 0.2126) + (pixels[i + 1] * 0.7152) + (pixels[i + 2] * 0.0722) >= 8) lit += 1;
        }
        return lit / Math.max(1, pixels.length / 4) < 0.01;
    } catch {
        return false;
    }
}

function canvasToDataUrl(canvas, quality) {
    return new Promise((resolve, reject) => {
        if (!canvas.toBlob) {
            try { resolve(canvas.toDataURL("image/jpeg", quality)); }
            catch (error) { reject(error); }
            return;
        }
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error("Frame encoding returned no data."));
                return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error || new Error("Could not read encoded frame."));
            reader.readAsDataURL(blob);
        }, "image/jpeg", quality);
    });
}

function isRetriableUpload(error) {
    const status = Number(error?.response?.status || 0);
    return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
}

const uploadRequestId = () => globalThis.crypto?.randomUUID?.() || `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function uploadRetryDelay(error, attempt) {
    const retryAfter = Number(error?.response?.headers?.["retry-after"] || 0) * 1000;
    const base = UPLOAD_RETRY_DELAYS_MS[Math.min(attempt, UPLOAD_RETRY_DELAYS_MS.length - 1)] || 0;
    return Math.max(retryAfter, base) + Math.round(Math.random() * 600);
}

async function postFormWithRetry(url, formFactory, { requestId = uploadRequestId(), onAttempt } = {}) {
    let lastError;
    for (let attempt = 0; attempt < UPLOAD_RETRY_DELAYS_MS.length; attempt += 1) {
        const delay = attempt ? uploadRetryDelay(lastError, attempt) : 0;
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        try {
            onAttempt?.({ requestId, attempt: attempt + 1, status: "started" });
            const response = await api.post(url, formFactory(), { headers: {
                "Content-Type": "multipart/form-data",
                "X-Upload-Request-ID": requestId,
                "X-Idempotency-Key": requestId,
            }});
            onAttempt?.({ requestId, attempt: attempt + 1, status: "completed" });
            return response;
        } catch (error) {
            lastError = error;
            onAttempt?.({ requestId, attempt: attempt + 1, status: "failed", httpStatus: error?.response?.status || 0 });
            if (!isRetriableUpload(error)) break;
        }
    }
    throw lastError;
}

async function postJsonWithRetry(url, body, { requestId = uploadRequestId(), onAttempt } = {}) {
    let lastError;
    for (let attempt = 0; attempt < UPLOAD_RETRY_DELAYS_MS.length; attempt += 1) {
        const delay = attempt ? uploadRetryDelay(lastError, attempt) : 0;
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        try {
            onAttempt?.({ requestId, attempt: attempt + 1, status: "started" });
            const response = await api.post(url, body, { headers: {
                "X-Upload-Request-ID": requestId,
                "X-Idempotency-Key": requestId,
            }});
            onAttempt?.({ requestId, attempt: attempt + 1, status: "completed" });
            return response;
        } catch (error) {
            lastError = error;
            onAttempt?.({ requestId, attempt: attempt + 1, status: "failed", httpStatus: error?.response?.status || 0 });
            if (!isRetriableUpload(error)) break;
        }
    }
    throw lastError;
}

async function putJsonWithRetry(url, body, { requestId = uploadRequestId(), onAttempt } = {}) {
    let lastError;
    for (let attempt = 0; attempt < UPLOAD_RETRY_DELAYS_MS.length; attempt += 1) {
        const delay = attempt ? uploadRetryDelay(lastError, attempt) : 0;
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        try {
            onAttempt?.({ requestId, attempt: attempt + 1, status: "started" });
            const response = await api.put(url, body, { headers: {
                "X-Request-ID": requestId,
                "X-Idempotency-Key": requestId,
            }});
            onAttempt?.({ requestId, attempt: attempt + 1, status: "completed" });
            return response;
        } catch (error) {
            lastError = error;
            onAttempt?.({ requestId, attempt: attempt + 1, status: "failed", httpStatus: error?.response?.status || 0 });
            if (!isRetriableUpload(error)) break;
        }
    }
    throw lastError;
}

const boundedNumber = (value, min, max, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
};

function normalizeControllerSummary(summary) {
    return {
        game: String(summary?.game || "warzone").slice(0, 50),
        duration_sec: Math.round(boundedNumber(summary?.duration_sec, 0, 86_400)),
        connected: Boolean(summary?.connected),
        controller_id: String(summary?.controller_id || "Standard controller").slice(0, 120),
        mapping: String(summary?.mapping || "standard").slice(0, 30),
        sample_count: Math.round(boundedNumber(summary?.sample_count, 0, 5_000_000)),
        active_ratio: boundedNumber(summary?.active_ratio, 0, 1),
        aim_intensity: boundedNumber(summary?.aim_intensity, 0, 1),
        movement_intensity: boundedNumber(summary?.movement_intensity, 0, 1),
        centering_ratio: boundedNumber(summary?.centering_ratio, 0, 1),
        reversal_rate: boundedNumber(summary?.reversal_rate, 0, 10),
        micro_correction_ratio: boundedNumber(summary?.micro_correction_ratio, 0, 1),
        saturation_ratio: boundedNumber(summary?.saturation_ratio, 0, 1),
        trigger_presses: Math.round(boundedNumber(summary?.trigger_presses, 0, 1_000_000)),
        button_presses: Math.round(boundedNumber(summary?.button_presses, 0, 1_000_000)),
        reaction_ms: summary?.reaction_ms == null ? null : Math.round(boundedNumber(summary.reaction_ms, 0, 5000)),
        fatigue_delta: boundedNumber(summary?.fatigue_delta, -1, 1),
        sampling_gaps: Math.round(boundedNumber(summary?.sampling_gaps, 0, 1_000_000)),
        missed_samples: Math.round(boundedNumber(summary?.missed_samples, 0, 5_000_000)),
        hidden_sampling_gaps: Math.round(boundedNumber(summary?.hidden_sampling_gaps, 0, 1_000_000)),
        largest_gap_ms: Math.round(boundedNumber(summary?.largest_gap_ms, 0, 86_400_000)),
        scheduler_pauses: Math.round(boundedNumber(summary?.scheduler_pauses, 0, 1_000_000)),
        scheduler_missed_samples: Math.round(boundedNumber(summary?.scheduler_missed_samples, 0, 5_000_000)),
        largest_scheduler_pause_ms: Math.round(boundedNumber(summary?.largest_scheduler_pause_ms, 0, 86_400_000)),
        hidden_ratio: boundedNumber(summary?.hidden_ratio, 0, 1),
        last_connected_at: summary?.last_connected_at ? String(summary.last_connected_at).slice(0, 60) : null,
    };
}

async function uploadSessionResumable(blob, metadata, onAttempt) {
    const totalChunks = Math.ceil(blob.size / SESSION_UPLOAD_CHUNK_BYTES);
    const initBody = {
        ...metadata,
        content_type: blob.type || "video/webm",
        total_bytes: blob.size,
        total_chunks: totalChunks,
    };
    let uploadId;
    let lastChunkError;
    // Re-initializing is an idempotent status/resume operation. If a proxy
    // drops one response, the next pass skips every chunk already accepted.
    for (let pass = 1; pass <= 4; pass += 1) {
        const init = await postJsonWithRetry("/sessions/upload/init", initBody, {
            requestId: `${metadata.capture_session_id}:init`, onAttempt,
        });
        if (init.data?.completed) return init;
        uploadId = init.data.upload_id;
        const received = new Set((init.data.received_chunks || []).map(Number));
        lastChunkError = null;
        for (let index = 0; index < totalChunks; index += 1) {
            if (received.has(index)) continue;
            const start = index * SESSION_UPLOAD_CHUNK_BYTES;
            const chunk = blob.slice(start, Math.min(blob.size, start + SESSION_UPLOAD_CHUNK_BYTES), blob.type);
            try {
                await postFormWithRetry(`/sessions/upload/${uploadId}/chunks/${index}`, () => {
                    const form = new FormData();
                    form.append("file", chunk, `${metadata.filename}.part-${index}`);
                    return form;
                }, { requestId: `${metadata.capture_session_id}:chunk:${index}`, onAttempt });
            } catch (error) {
                lastChunkError = error;
                break;
            }
        }
        if (!lastChunkError) break;
        if (pass === 4) throw lastChunkError;
        await new Promise((resolve) => window.setTimeout(resolve, Math.min(30000, pass * 7500)));
    }
    for (let completionAttempt = 1; completionAttempt <= 3; completionAttempt += 1) {
        await postJsonWithRetry(`/sessions/upload/${uploadId}/complete`, {}, {
            requestId: `${metadata.capture_session_id}:complete:${completionAttempt}`, onAttempt,
        });
        for (let poll = 0; poll < 120; poll += 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 3000));
            const status = await api.get(`/sessions/upload/${uploadId}/status`);
            if (status.data?.status === "completed" && status.data?.upload_receipt) return status;
            if (status.data?.status === "failed") {
                onAttempt?.({
                    requestId: `${metadata.capture_session_id}:assembly`,
                    attempt: completionAttempt,
                    status: "failed",
                    httpStatus: 0,
                });
                break;
            }
        }
    }
    throw new Error("Recording chunks uploaded, but server assembly did not complete after three attempts.");
}

async function extractWebmInitSegment(blob) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    outer: for (let i = 0; i <= bytes.length - WEBM_CLUSTER_ID.length; i += 1) {
        for (let j = 0; j < WEBM_CLUSTER_ID.length; j += 1) {
            if (bytes[i + j] !== WEBM_CLUSTER_ID[j]) continue outer;
        }
        return i > 0 ? blob.slice(0, i, blob.type || "video/webm") : null;
    }
    return null;
}

// Adaptive cadence (ms): faster during combat, paused in lobby/menu. Tied to the coaching level.
const CADENCE = {
    // Elimination banners are brief. Nine-to-fourteen second sampling gaps missed
    // real highlights in long sessions, so active-play cadence is kept below 8s.
    minimal: { combat: 4500, match: 7500, idle: 18000 },
    balanced: { combat: 3000, match: 5000, idle: 16000 },
    intense: { combat: 2500, match: 4000, idle: 14000 },
};
const SILENT_STATES = new Set(["lobby", "menu", "loadout", "scoreboard", "spectating", "dead", "killcam"]);

function nextInterval(level, state, enemies) {
    const c = CADENCE[level] || CADENCE.balanced;
    if (state === "in_combat" || enemies > 0) return Math.min(c.combat, 2500);
    // Confirm end-of-match results quickly enough to preserve the victory highlight.
    if (state === "scoreboard") return 1500;
    if (SILENT_STATES.has(state) || state === "loading") return c.idle;
    return c.match;
}

const SOURCES = [
    { id: "screen", label: "Screen Capture", icon: ScreenShare, desc: "Best for PC — full AI coaching" },
    { id: "stream", label: "Stream URL", icon: Link2, desc: "HLS / m3u8 live stream" },
    { id: "twitch", label: "Twitch", icon: Twitch, desc: "Console players via Twitch" },
];

export default function LiveCapture() {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const fileRef = useRef(null);
    const hlsRef = useRef(null);
    const canvasRef = useRef(null);
    const frameTimerRef = useRef(null);
    const analyzeRef = useRef(() => {});
    const capturingRef = useRef(false);
    const elapsedRef = useRef(0);
    const markersRef = useRef([]);          // AI auto-clip markers within the session (for future Studio)
    const frameStatsRef = useRef({ total: 0, empty: 0, black: 0 });
    const lastFrameBlackRef = useRef(false);
    const rollingRef = useRef([]);          // selected pre-trigger window of independently timestamped chunks
    const pendingClipsRef = useRef(new Map());
    const initSegmentPromiseRef = useRef(null);
    const recorderMimeRef = useRef("video/webm");
    const lastStateRef = useRef("in_match");
    const intervalRef = useRef(0);
    const controllerResearchRef = useRef(null);
    const controllerTelemetryRef = useRef({ connected: null, lastAt: 0 });
    const recordingFinalizeRef = useRef(null);
    const recordingChunkIndexRef = useRef(0);
    const recordingPersistenceRef = useRef(Promise.resolve());
    const recoveryInFlightRef = useRef(false);
    const analysisInFlightRef = useRef(false);
    const latencyGuardRef = useRef({ degradedUntil: 0, lastNotifiedAt: 0, maxBlockedMs: 0 });
    const stoppingRef = useRef(false);
    const stopTimeoutRef = useRef(null);
    const captureSessionIdRef = useRef("");
    const uploadAttemptsRef = useRef(0);
    const blackFrameWarningRef = useRef(false);
    const lastClipDecisionTelemetryRef = useRef(0);
    const lastAiFailureTelemetryRef = useRef(0);
    const scoreboardBurstTimerRef = useRef(null);
    const scoreboardProgressRef = useRef({ confirmations: 0, notifiedAt: 0 });

    const coach = useLiveCoach();
    const { user } = useAuth();
    const hasPro = user?.entitlements?.has_pro;

    const [games, setGames] = useState([]);
    const [game, setGame] = useState("warzone");
    const [source, setSource] = useState("screen");
    const [streamUrl, setStreamUrl] = useState("");
    const [twitchChannel, setTwitchChannel] = useState("");
    const [twitchActive, setTwitchActive] = useState(false);
    const [buffer, setBuffer] = useState(60);
    const [event, setEvent] = useState("Hotkey Trigger");
    const [capturing, setCapturing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [streamBlocked, setStreamBlocked] = useState(false);
    const [captureBlackWarning, setCaptureBlackWarning] = useState(false);
    const [controllerStatus, setControllerStatus] = useState({ connected: false, samples: 0, collecting: false });
    const [sessionUploadStatus, setSessionUploadStatus] = useState("idle");
    const [latencySafeguard, setLatencySafeguard] = useState(false);

    const engageLatencySafeguard = (durationMs, reason) => {
        const now = Date.now();
        const guard = latencyGuardRef.current;
        guard.degradedUntil = Math.max(guard.degradedUntil, now + LATENCY_GUARD_WINDOW_MS);
        guard.maxBlockedMs = Math.max(guard.maxBlockedMs, Math.round(durationMs || 0));
        setLatencySafeguard(true);
        if (now - guard.lastNotifiedAt >= 30_000) {
            guard.lastNotifiedAt = now;
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "capture_health", level: "warning",
                message: `Latency safeguard engaged: ${reason}; ${Math.round(durationMs || 0)}ms. ` +
                    "AI frame resolution and cadence reduced while full recording continues.",
                duration_ms: Math.round(durationMs || 0),
                capture_session_id: captureSessionIdRef.current,
            }}));
        }
    };

    useEffect(() => {
        if (typeof PerformanceObserver === "undefined") return undefined;
        let observer;
        try {
            observer = new PerformanceObserver((list) => {
                if (!capturingRef.current) return;
                for (const entry of list.getEntries()) {
                    if (entry.duration >= LONG_TASK_DEGRADE_MS) {
                        engageLatencySafeguard(entry.duration, "browser main thread blocked");
                    }
                }
            });
            observer.observe({ type: "longtask", buffered: false });
        } catch { return undefined; }
        return () => observer?.disconnect();
        // Observer is intentionally installed once and reads mutable capture refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!["uploading", "recovering", "persisting"].includes(sessionUploadStatus)) return undefined;
        const protectUpload = (event) => {
            event.preventDefault();
            event.returnValue = "Your gameplay recording is still uploading.";
            return event.returnValue;
        };
        window.addEventListener("beforeunload", protectUpload);
        return () => window.removeEventListener("beforeunload", protectUpload);
    }, [sessionUploadStatus]);

    if (!controllerResearchRef.current) {
        controllerResearchRef.current = createControllerResearch((status) => {
            setControllerStatus(status);
            const now = Date.now();
            const previous = controllerTelemetryRef.current;
            const connectionChanged = previous.connected !== status.connected;
            if (connectionChanged || now - previous.lastAt >= 5000 || status.reason === "stopped") {
                controllerTelemetryRef.current = { connected: status.connected, lastAt: now };
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "controller_status",
                    level: status.connected ? "info" : status.collecting ? "warning" : "info",
                    message: `Controller ${status.connected ? "detected" : "not detected"}; ` +
                        `${status.samples || 0} connected samples; ${status.samplingGaps || 0} sampling gaps; ` +
                        `${status.schedulerPauses || 0} browser scheduling pauses; ` +
                        `reason ${status.reason || "status"}`,
                    duration_ms: status.largestGapMs || 0,
                }}));
            }
        });
    }

    useEffect(() => {
        controllerResearchRef.current?.prime();
        api.get("/games").then(({ data }) => setGames(data.games));
        api.get("/settings").then(({ data }) => {
            if (data.stream_source) setSource(data.stream_source === "screen" ? "screen" : data.stream_source);
            if (data.stream_url) setStreamUrl(data.stream_url);
            if (data.twitch_channel) setTwitchChannel(data.twitch_channel);
        }).catch(() => {});
        return () => {
            fullStop();
            controllerResearchRef.current?.destroy();
            if (stopTimeoutRef.current) window.clearTimeout(stopTimeoutRef.current);
            if (capturingRef.current) api.put("/session", { monitoring: false }).catch(() => {});
            if ("speechSynthesis" in window) window.speechSynthesis.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!capturing) return;
        const t = setInterval(() => setElapsed((e) => { const n = e + 1; elapsedRef.current = n; return n; }), 1000);
        return () => clearInterval(t);
    }, [capturing]);

    // Grab a JPEG data URL of the current frame. Prefers ImageCapture on the screen track so it keeps
    // working when the browser tab is NOT focused (the player is in their game).
    const grabFrame = async () => {
        // Keep AI vision useful without repeatedly copying a full-resolution
        // gameplay frame on the browser's main thread.
        const guarded = Date.now() < latencyGuardRef.current.degradedUntil;
        const maxW = guarded ? 512 : 960;
        const jpegQuality = guarded ? 0.30 : 0.48;
        const track = streamRef.current?.getVideoTracks?.()[0];
        if (source === "screen" && track && typeof window.ImageCapture !== "undefined") {
            try {
                const bmp = await new window.ImageCapture(track).grabFrame();
                const scale = Math.min(1, maxW / bmp.width);
                const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
                const canvas = canvasRef.current || (canvasRef.current = document.createElement("canvas"));
                canvas.width = w; canvas.height = h;
                canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
                bmp.close?.();
                lastFrameBlackRef.current = canvasIsEffectivelyBlack(canvas);
                return await canvasToDataUrl(canvas, jpegQuality);
            } catch { /* fall through to <video> grab */ }
        }
        const v = videoRef.current;
        if (!v || v.videoWidth === 0 || v.readyState < 2) return null;
        const scale = Math.min(1, maxW / v.videoWidth);
        const w = Math.round(v.videoWidth * scale), h = Math.round(v.videoHeight * scale);
        const canvas = canvasRef.current || (canvasRef.current = document.createElement("canvas"));
        canvas.width = w; canvas.height = h;
        try {
            canvas.getContext("2d").drawImage(v, 0, 0, w, h);
            lastFrameBlackRef.current = canvasIsEffectivelyBlack(canvas);
            return await canvasToDataUrl(canvas, jpegQuality);
        } catch {
            setStreamBlocked(true);
            stopFrameLoop();
            toast.error("This stream blocks AI frame reading (CORS). Use Screen Capture instead.");
            return "__BLOCKED__";
        }
    };

    const setMarkerStatus = (clipId, status) => {
        const marker = markersRef.current.find((m) => m.clip_id === clipId);
        if (marker) marker.status = status;
    };

    const attachClipVideo = async (clipId, blob, durationSec) => {
        const filename = `clip-${Date.now()}.webm`;
        await postFormWithRetry(`/clips/${clipId}/attach-video?duration_sec=${durationSec}`, () => {
            const fd = new FormData();
            fd.append("file", blob, filename);
            return fd;
        });
    };

    const finalizePendingClip = async (clipId, force = false) => {
        const pending = pendingClipsRef.current.get(clipId);
        if (!pending) return null;
        if (pending.uploadPromise) return pending.uploadPromise;
        if (!force && Date.now() < pending.deadline) return null;
        pending.uploadPromise = (async () => {
            try {
                const pieces = pending.chunks.map((c) => c.blob);
                const hasHeader = pending.chunks.some((c) => c.hasHeader);
                if (!hasHeader) {
                    const init = await initSegmentPromiseRef.current;
                    if (init?.size) pieces.unshift(init);
                }
                if (!pieces.length) throw new Error("No recorded media was available for the highlight window.");
                const blob = new Blob(pieces, { type: recorderMimeRef.current || "video/webm" });
                await attachClipVideo(clipId, blob, pending.durationSec);
                setMarkerStatus(clipId, "ready");
                toast.success(`Auto-clip ready: ${pending.title}`);
            } catch (error) {
                setMarkerStatus(clipId, "failed");
                console.error("Auto-clip processing failed", error);
                toast.error(`Highlight detected, but its video is still processing from the full session.`);
            } finally {
                pendingClipsRef.current.delete(clipId);
            }
        })();
        return pending.uploadPromise;
    };

    const queueAutoClip = (clip) => {
        if (!clip?.id || pendingClipsRef.current.has(clip.id)) return;
        const now = Date.now();
        const clipSpan = clipWindow(buffer);
        const preChunks = rollingRef.current
            .filter((chunk) => chunk.t >= now - clipSpan.before * 1000)
            .map((chunk) => ({ ...chunk }));
        const marker = {
            clip_id: clip.id,
            at_sec: elapsedRef.current,
            title: clip.title || "Highlight",
            type: "ai_auto_clip",
            before_sec: clipSpan.before,
            after_sec: clipSpan.after,
            status: "capturing",
        };
        markersRef.current.push(marker);
        if (captureSessionIdRef.current) {
            recordingPersistenceRef.current = recordingPersistenceRef.current
                .then(() => patchPendingRecording(captureSessionIdRef.current, {
                    markers: markersRef.current.slice(),
                }))
                .catch(() => {});
        }
        pendingClipsRef.current.set(clip.id, {
            clipId: clip.id,
            title: marker.title,
            triggerAt: now,
            deadline: now + clipSpan.after * 1000,
            durationSec: clipSpan.total,
            chunks: preChunks,
            uploadPromise: null,
        });
        // MediaRecorder data events finalize the clip in background tabs; this is a foreground fallback.
        window.setTimeout(() => finalizePendingClip(clip.id).catch(() => {}), clipSpan.after * 1000 + 1250);
        toast.success(`Highlight detected — capturing ${clipSpan.after}s after the play.`);
    };

    const flushPendingClips = async () => {
        const ids = Array.from(pendingClipsRef.current.keys());
        await Promise.allSettled(ids.map((id) => finalizePendingClip(id, true)));
    };

    const applyCadence = (state, enemies) => {
        const base = nextInterval(coach.level, state, enemies);
        const guarded = Date.now() < latencyGuardRef.current.degradedUntil;
        const want = guarded
            ? Math.max(base, state === "scoreboard" ? 2000 : 7000)
            : base;
        if (want !== intervalRef.current) {
            intervalRef.current = want;
            stopFrameLoop();
            frameTimerRef.current = createTicker(want, () => analyzeRef.current());
        }
    };

    // Keep the analyze closure fresh (ticker always calls latest game/model/level).
    analyzeRef.current = async () => {
        if (analysisInFlightRef.current) return;
        analysisInFlightRef.current = true;
        const analysisStartedAt = performance.now();
        let frame;
        try { frame = await grabFrame(); }
        catch { analysisInFlightRef.current = false; return; }
        if (frame === "__BLOCKED__" || !frame || frame.length < 1000) {
            analysisInFlightRef.current = false;
            return;
        }
        coach.setAnalyzing(true);
        try {
            const { data } = await api.post("/monitor/analyze", { frame, game, model: coach.model });
            const state = data.game_state || "in_match";
            lastStateRef.current = state;
            if (state === "scoreboard" && !data.scoreboard_verified) {
                const confirmations = Number(data.scoreboard_confirmations || 0);
                scoreboardProgressRef.current.confirmations = confirmations;
                if (!scoreboardBurstTimerRef.current) {
                    scoreboardBurstTimerRef.current = window.setTimeout(() => {
                        scoreboardBurstTimerRef.current = null;
                        if (capturingRef.current) analyzeRef.current();
                    }, 900);
                }
                if (confirmations === 1 && Date.now() - scoreboardProgressRef.current.notifiedAt > 5000) {
                    scoreboardProgressRef.current.notifiedAt = Date.now();
                    toast("Scoreboard found — hold it on screen for one verification frame.");
                }
            } else if (data.scoreboard_verified && scoreboardBurstTimerRef.current) {
                window.clearTimeout(scoreboardBurstTimerRef.current);
                scoreboardBurstTimerRef.current = null;
            }
            if (data.analysis_status === "fallback" && Date.now() - lastAiFailureTelemetryRef.current >= 60_000) {
                lastAiFailureTelemetryRef.current = Date.now();
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "ai_provider", level: "error",
                    message: `Live AI provider unavailable for ${data.analysis_model || coach.model}; recording continues but cues and automatic highlight detection are degraded`,
                    capture_session_id: captureSessionIdRef.current,
                }}));
                toast.error("Live AI is unavailable. Recording continues, but automatic clips are degraded.");
            }
            const enemyCount = (data.enemies || []).length;
            if (data.silent) coach.clearQueue();
            else if (data.cues?.length) coach.ingestCues(data.cues);
            controllerResearchRef.current?.noteEvents(data.events || []);
            frameStatsRef.current.total += 1;
            if (lastFrameBlackRef.current) frameStatsRef.current.black += 1;
            const frameStats = frameStatsRef.current;
            if (frameStats.total >= 3 && frameStats.black === frameStats.total && !blackFrameWarningRef.current) {
                blackFrameWarningRef.current = true;
                setCaptureBlackWarning(true);
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "capture_health", level: "error",
                    message: `Capture health warning: ${frameStats.black}/${frameStats.total} sampled frames were black`,
                    capture_session_id: captureSessionIdRef.current,
                }}));
                toast.error("Black capture detected. Re-share the entire display before continuing gameplay.");
            } else if (!lastFrameBlackRef.current && blackFrameWarningRef.current) {
                blackFrameWarningRef.current = false;
                setCaptureBlackWarning(false);
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "capture_health", level: "info",
                    message: "Capture health recovered: visible gameplay frames resumed",
                    capture_session_id: captureSessionIdRef.current,
                }}));
            }
            const notable = ((data.events?.length || 0) + enemyCount) > 0;
            if (!notable) frameStatsRef.current.empty += 1;
            const decisionNow = Date.now();
            const shouldReportClipDecision = data.clip_created
                || Number(data.clip_score || 0) >= 60
                || decisionNow - lastClipDecisionTelemetryRef.current >= 60_000;
            if (shouldReportClipDecision) {
                lastClipDecisionTelemetryRef.current = decisionNow;
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "ai_clip_decision",
                    level: "info",
                    message: `AI clip ${data.clip_created ? "created" : "evaluated"}: score ${data.clip_score || 0}/${data.clip_threshold || 72}; model ${data.clip_model_score || 0}; rules ${data.clip_rule_score || 0}; evidence ${data.clip_evidence_count || 0}; reason ${data.clip_reason || "unknown"}; state ${state}`,
                    capture_session_id: captureSessionIdRef.current,
                }}));
            }
            if (data.clip) {
                if (source === "screen") queueAutoClip(data.clip);
                else markersRef.current.push({ clip_id: data.clip.id, at_sec: elapsedRef.current,
                    title: data.clip.title || "Highlight", type: "ai_auto_clip", status: "image_only" });
            }
            applyCadence(state, enemyCount); // speed up in combat, ease off in menus
        } catch (error) {
            if (Date.now() - lastAiFailureTelemetryRef.current >= 60_000) {
                lastAiFailureTelemetryRef.current = Date.now();
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "ai_provider", level: "error",
                    message: `Live AI request failed; status ${error?.response?.status || 0}; recording continues`,
                    status: error?.response?.status || 0,
                    capture_session_id: captureSessionIdRef.current,
                }}));
            }
        }
        finally {
            const duration = performance.now() - analysisStartedAt;
            if (duration >= SLOW_ANALYSIS_DEGRADE_MS) {
                engageLatencySafeguard(duration, "slow AI frame cycle");
            } else if (latencyGuardRef.current.degradedUntil && Date.now() >= latencyGuardRef.current.degradedUntil) {
                latencyGuardRef.current.degradedUntil = 0;
                setLatencySafeguard(false);
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "capture_health", level: "info",
                    message: "Latency safeguard recovered: normal AI cadence restored",
                    capture_session_id: captureSessionIdRef.current,
                }}));
            }
            analysisInFlightRef.current = false;
            coach.setAnalyzing(false);
        }
    };

    const startFrameLoop = () => {
        stopFrameLoop();
        setStreamBlocked(false);
        intervalRef.current = nextInterval(coach.level, "in_match", 0);
        // Background-safe ticker (Web Worker) so frames keep flowing while the tab is unfocused.
        frameTimerRef.current = createTicker(intervalRef.current, () => analyzeRef.current());
        setTimeout(() => analyzeRef.current(), 2500); // first pass after stream stabilizes
    };
    const stopFrameLoop = () => {
        if (frameTimerRef.current) { frameTimerRef.current.stop(); frameTimerRef.current = null; }
        if (scoreboardBurstTimerRef.current) {
            window.clearTimeout(scoreboardBurstTimerRef.current);
            scoreboardBurstTimerRef.current = null;
        }
    };

    const stopTracks = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };

    const fullStop = () => { stopFrameLoop(); stopTracks(); };

    const beginSession = async () => {
        captureSessionIdRef.current = globalThis.crypto?.randomUUID?.() || `capture-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        uploadAttemptsRef.current = 0;
        setCapturing(true);
        capturingRef.current = true;
        setElapsed(0);
        elapsedRef.current = 0;
        markersRef.current = [];
        pendingClipsRef.current.clear();
        initSegmentPromiseRef.current = null;
        frameStatsRef.current = { total: 0, empty: 0, black: 0 };
        blackFrameWarningRef.current = false;
        lastClipDecisionTelemetryRef.current = 0;
        lastAiFailureTelemetryRef.current = 0;
        scoreboardProgressRef.current = { confirmations: 0, notifiedAt: 0 };
        setCaptureBlackWarning(false);
        setSessionUploadStatus("idle");
        setLatencySafeguard(false);
        latencyGuardRef.current = { degradedUntil: 0, lastNotifiedAt: 0, maxBlockedMs: 0 };
        recordingChunkIndexRef.current = 0;
        recordingPersistenceRef.current = Promise.resolve();
        if (source === "screen") {
            // Ask Chrome to retain interrupted capture data across storage
            // pressure/restarts. Browsers may decline; IndexedDB still works.
            navigator.storage?.persist?.().catch(() => {});
            recordingPersistenceRef.current = startPendingRecording({
                capture_session_id: captureSessionIdRef.current,
                game,
                filename: `session-${Date.now()}.webm`,
                content_type: recorderMimeRef.current || "video/webm",
                started_at: new Date().toISOString(),
                markers: [],
                frame_total: 0,
                frame_empty: 0,
                frame_black: 0,
                duration_sec: 0,
            }).catch((error) => {
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "capture_health", level: "warning",
                    message: `Local recording recovery unavailable: ${error?.message || "storage error"}`,
                    capture_session_id: captureSessionIdRef.current,
                }}));
            });
        }
        controllerResearchRef.current?.start(game);
        startFrameLoop();
        window.dispatchEvent(new CustomEvent("ww:session", { detail: {
            monitoring: true, ai_status: "watching", game,
            capture_session_id: captureSessionIdRef.current,
        } }));
        await putJsonWithRetry(
            "/session",
            {
                monitoring: true,
                current_game: game,
                buffer_seconds: buffer,
                capture_session_id: captureSessionIdRef.current,
            },
            { requestId: `${captureSessionIdRef.current}:session-start` },
        );
        window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
            kind: "capture_status", level: "info",
            message: `Capture started; source ${source}; game ${game}; controller ${controllerStatus.connected ? "detected" : "awaiting_input"}`,
            capture_session_id: captureSessionIdRef.current,
        }}));
    };

    // ---------- Screen capture ----------
    const startScreen = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    frameRate: { ideal: 30, max: 30 },
                    width: { ideal: 1920, max: 1920 },
                    height: { ideal: 1080, max: 1080 },
                },
                audio: true,
            });
            streamRef.current = stream;
            const videoTrack = stream.getVideoTracks()[0];
            try {
                if (videoTrack && "contentHint" in videoTrack) videoTrack.contentHint = "motion";
            } catch { /* content hints are an optional browser optimization */ }
            if (videoRef.current) videoRef.current.srcObject = stream;
            const mimeType = preferredRecorderMime();
            const rec = createSessionRecorder(stream, mimeType);
            recorderMimeRef.current = rec.mimeType || mimeType || "video/webm";
            chunksRef.current = [];
            rollingRef.current = [];
            rec.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    const chunkIndex = recordingChunkIndexRef.current;
                    recordingChunkIndexRef.current += 1;
                    const isFirst = chunkIndex === 0;
                    const entry = { t: Date.now(), blob: e.data, hasHeader: isFirst };
                    chunksRef.current.push(e.data);
                    const sessionId = captureSessionIdRef.current;
                    if (sessionId) {
                        recordingPersistenceRef.current = recordingPersistenceRef.current
                            .then(() => appendPendingRecordingChunk(sessionId, chunkIndex, e.data))
                            .catch((error) => {
                                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                                    kind: "capture_health", level: "warning",
                                    message: `Recording recovery chunk ${chunkIndex} was not persisted: ${error?.message || "storage error"}`,
                                    capture_session_id: sessionId,
                                }}));
                            });
                    }
                    rollingRef.current.push(entry);
                    if (isFirst) initSegmentPromiseRef.current = extractWebmInitSegment(e.data);
                    const cutoff = Date.now() - (buffer + 3) * 1000;
                    while (rollingRef.current.length && rollingRef.current[0].t < cutoff) rollingRef.current.shift();
                    const due = [];
                    for (const pending of pendingClipsRef.current.values()) {
                        if (entry.t > pending.triggerAt && entry.t <= pending.deadline + 1500) {
                            pending.chunks.push(entry);
                        }
                        if (entry.t >= pending.deadline) due.push(pending.clipId);
                    }
                    due.forEach((id) => finalizePendingClip(id).catch(() => {}));
                }
            };
            rec.start(RECORDER_TIMESLICE_MS);
            recorderRef.current = rec;
            await beginSession();
            videoTrack?.addEventListener("ended", stopCapture);
            toast.success("Screen capture live. Tactical AI is watching your gameplay.");
        } catch (error) {
            try {
                if (recorderRef.current?.state !== "inactive") recorderRef.current?.stop();
            } catch { /* the display track is stopped below */ }
            recorderRef.current = null;
            chunksRef.current = [];
            rollingRef.current = [];
            fullStop();
            setCapturing(false);
            capturingRef.current = false;
            controllerResearchRef.current?.stop();
            toast.error(error?.response ? "Monitoring could not start after server retries." : "Screen capture permission denied or unavailable.");
        }
    };

    // ---------- HLS / direct stream URL ----------
    const startStream = async () => {
        const url = streamUrl.trim();
        if (!url) { toast.error("Enter a stream URL (.m3u8 or direct video)."); return; }
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = null;
        v.crossOrigin = "anonymous";
        persist({ stream_source: "stream", stream_url: url });
        try {
            if (url.includes(".m3u8") && Hls.isSupported()) {
                const hls = new Hls({ xhrSetup: (xhr) => { xhr.withCredentials = false; } });
                hlsRef.current = hls;
                hls.loadSource(url);
                hls.attachMedia(v);
                hls.on(Hls.Events.ERROR, (_e, d) => { if (d.fatal) toast.error("Stream failed to load. Check the URL."); });
            } else {
                v.src = url;
            }
            await v.play().catch(() => {});
            await beginSession();
            toast.success("Stream monitoring live. AI will analyze frames if the source allows.");
        } catch {
            toast.error("Could not start the stream.");
        }
    };

    // ---------- Twitch embed (view only) ----------
    const startTwitch = () => {
        const ch = twitchChannel.trim().replace(/^.*twitch\.tv\//i, "").replace(/[^a-zA-Z0-9_]/g, "");
        if (!ch) { toast.error("Enter your Twitch channel name."); return; }
        setTwitchChannel(ch);
        setTwitchActive(true);
        persist({ stream_source: "twitch", twitch_channel: ch });
        toast("Twitch loaded. For AI coaching, use Screen Capture on this tab.");
    };

    const startCapture = () => {
        if (source === "screen") return startScreen();
        if (source === "stream") return startStream();
        return startTwitch();
    };

    // Save the full screen-capture recording (entire session) with AI auto-clip markers + low-event flag.
    const saveFullSession = async () => {
        const chunks = chunksRef.current;
        recorderRef.current = null;
        if (!chunks || chunks.length === 0) {
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "upload_status", level: "error", message: "Full-session save skipped: no recorder chunks were available",
            }}));
            return false;
        }
        const markers = markersRef.current.slice();
        const { total, empty, black } = frameStatsRef.current;
        const dur = elapsedRef.current;
        const captureSessionId = captureSessionIdRef.current;
        setSessionUploadStatus("persisting");
        await recordingPersistenceRef.current.catch(() => {});
        const filename = `session-${captureSessionId}.webm`;
        await patchPendingRecording(captureSessionId, {
            status: "ready",
            filename,
            content_type: recorderMimeRef.current || "video/webm",
            game,
            duration_sec: dur,
            frame_total: total,
            frame_empty: empty,
            frame_black: black,
            markers,
            latency_guard_max_ms: latencyGuardRef.current.maxBlockedMs,
        }).catch(() => {});
        uploadAttemptsRef.current += 1;
        const uploadAttempt = uploadAttemptsRef.current;
        if (total >= 5 && black >= total) {
            setSessionUploadStatus("blocked");
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "capture_health", level: "error",
                message: `Recording upload blocked: ${black}/${total} analyzed frames were black. Re-select the entire display and confirm the live preview before retrying.`,
                capture_session_id: captureSessionId,
            }}));
            toast.error("Recording was not uploaded because capture analysis received only black/empty frames. Re-select the entire display and verify the preview.");
            return false;
        }
        try {
            const blob = new Blob(chunks, { type: recorderMimeRef.current || "video/webm" });
            const uploadChunks = Math.ceil(blob.size / SESSION_UPLOAD_CHUNK_BYTES);
            setSessionUploadStatus("uploading");
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "upload_status", level: "info",
                message: `Full-session upload started; ${blob.size} bytes; ${uploadChunks} upload chunks; ` +
                    `${chunks.length} recorder slices; ${dur}s; attempt ${uploadAttempt}`,
                capture_session_id: captureSessionId,
                bytes: blob.size,
                attempt_count: uploadAttempt,
            }}));
            const onUploadAttempt = ({ requestId, attempt, status, httpStatus }) => {
                if (status !== "failed" && !(status === "completed" && attempt > 1)) return;
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "upload_attempt", level: status === "failed" ? "warning" : "info",
                    message: `Recording request ${requestId}; attempt ${attempt}; ${status}; HTTP ${httpStatus || 0}`,
                    capture_session_id: captureSessionId,
                    attempt_count: attempt,
                    status: httpStatus || 0,
                }}));
            };
            const { data } = await uploadSessionResumable(blob, {
                capture_session_id: captureSessionId,
                filename,
                game,
                duration_sec: dur,
                frame_total: total,
                frame_empty: empty,
                frame_black: black,
                markers,
            }, onUploadAttempt);
            if (data.low_event) {
                toast.warning("Few notable events detected this session — check your capture window/source.");
            } else {
                toast.success(`Full session saved · ${markers.length} AI highlight${markers.length === 1 ? "" : "s"} marked.`);
            }
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "upload_status", level: "info",
                message: `Recording upload completed; receipt ${data.upload_receipt?.recording_id || data.id}; ` +
                    `request ${data.upload_request_id || `${captureSessionId}:complete`}; ` +
                    `status ${data.upload_receipt?.status || "accepted"}; ${dur}s; ${markers.length} markers`,
                capture_session_id: captureSessionId,
                artifact_id: data.upload_receipt?.recording_id || data.id,
                bytes: data.upload_receipt?.bytes || blob.size,
                attempt_count: uploadAttempt,
            }}));
            setSessionUploadStatus("saved");
            await deleteRecoverableRecording(captureSessionId).catch(() => {});
            chunksRef.current = [];
            markersRef.current = [];
            frameStatsRef.current = { total: 0, empty: 0, black: 0 };
            return true;
        } catch (error) {
            console.error("Full-session upload failed", error);
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "upload_status", level: "error",
                message: `Full-session upload failed; status ${error?.response?.status || 0}`,
                status: error?.response?.status || 0,
                capture_session_id: captureSessionId,
                attempt_count: uploadAttempt,
            }}));
            setSessionUploadStatus("failed");
            await patchPendingRecording(captureSessionId, { status: "ready" }).catch(() => {});
            toast.error("Full-session upload failed after retries. Use Retry Session Upload before closing this tab.");
            return false;
        }
    };

    const resumeStoredRecordings = async ({ manual = false } = {}) => {
        if (recoveryInFlightRef.current || capturingRef.current) return false;
        recoveryInFlightRef.current = true;
        setSaving(true);
        try {
            const sessions = await listRecoverableRecordings();
            if (!sessions.length) {
                if (manual) toast("No recoverable recording is waiting to upload.");
                return false;
            }
            let recovered = 0;
            for (const summary of sessions.slice(0, 3)) {
                const captureSessionId = summary.capture_session_id;
                const { session, chunks } = await loadRecoverableRecording(captureSessionId);
                if (!session || !chunks.length) continue;
                const blob = new Blob(chunks.map((chunk) => chunk.blob), {
                    type: session.content_type || "video/webm",
                });
                const duration = Math.max(1, Number(session.duration_sec || 0) || Math.round(
                    (Date.parse(session.updated_at || "") - Date.parse(session.started_at || "")) / 1000,
                ) || 1);
                setSessionUploadStatus("recovering");
                await patchPendingRecording(captureSessionId, { status: "uploading" }).catch(() => {});
                window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                    kind: "upload_status", level: "info",
                    message: `Resuming locally preserved recording; ${blob.size} bytes; ${chunks.length} recorder slices`,
                    capture_session_id: captureSessionId,
                    bytes: blob.size,
                }}));
                try {
                    const { data } = await uploadSessionResumable(blob, {
                        capture_session_id: captureSessionId,
                        filename: session.filename || `session-${captureSessionId}.webm`,
                        game: session.game || "warzone",
                        duration_sec: duration,
                        frame_total: Number(session.frame_total || 0),
                        frame_empty: Number(session.frame_empty || 0),
                        frame_black: Number(session.frame_black || 0),
                        markers: Array.isArray(session.markers) ? session.markers : [],
                    });
                    await deleteRecoverableRecording(captureSessionId);
                    recovered += 1;
                    window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                        kind: "upload_status", level: "info",
                        message: `Recovered recording upload completed; receipt ${data.upload_receipt?.recording_id || data.id}; status accepted`,
                        capture_session_id: captureSessionId,
                        artifact_id: data.upload_receipt?.recording_id || data.id,
                        bytes: data.upload_receipt?.bytes || blob.size,
                    }}));
                } catch (error) {
                    await patchPendingRecording(captureSessionId, { status: "ready" }).catch(() => {});
                    window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                        kind: "upload_status", level: "error",
                        message: `Recovered recording upload failed; status ${error?.response?.status || 0}`,
                        status: error?.response?.status || 0,
                        capture_session_id: captureSessionId,
                    }}));
                    throw error;
                }
            }
            if (recovered) {
                setSessionUploadStatus("saved");
                toast.success(`${recovered} interrupted recording${recovered === 1 ? "" : "s"} recovered. Safe to close.`);
                return true;
            }
            return false;
        } catch (error) {
            setSessionUploadStatus("failed");
            if (manual) toast.error("The preserved recording is still safe locally, but upload recovery failed.");
            return false;
        } finally {
            recoveryInFlightRef.current = false;
            setSaving(false);
        }
    };

    useEffect(() => {
        const timer = window.setTimeout(() => resumeStoredRecordings().catch(() => {}), 1500);
        return () => window.clearTimeout(timer);
        // Recovery runs once when Live Capture opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveControllerSummary = async (summary) => {
        if (!summary) return false;
        const payload = normalizeControllerSummary(summary);
        const detected = Boolean(payload.connected && payload.sample_count >= 30);
        const expectedSamples = payload.sample_count + payload.scheduler_missed_samples;
        const sampleCoverage = expectedSamples > 0
            ? Math.round((payload.sample_count / expectedSamples) * 100)
            : 0;
        const requestId = `${captureSessionIdRef.current}:controller`;
        try {
            const { data } = await postJsonWithRetry("/controller/sessions", {
                ...payload,
                capture_session_id: captureSessionIdRef.current,
                failure_reason: detected ? null : "browser_gamepad_not_detected",
            }, { requestId });
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "controller_status", level: detected ? "info" : "error",
                message: `Controller session saved as ${data.detection_status}; ${payload.sample_count} samples; ` +
                    `${payload.sampling_gaps || 0} real gaps; max gap ${payload.largest_gap_ms || 0}ms; ` +
                    `${payload.scheduler_pauses || 0} browser scheduling pauses; ${sampleCoverage}% sample coverage`,
                duration_ms: payload.largest_gap_ms || 0,
                capture_session_id: captureSessionIdRef.current,
                artifact_id: data.id,
            }}));
            if (detected) toast.success("Controller detected — performance analysis saved to Personal Improvement.");
            else toast.error("Controller was not detected by Chrome. The failed session was recorded for diagnosis.");
            return detected;
        } catch (error) {
            const detail = apiError(error?.response?.data?.detail);
            console.error("Controller session upload failed", {
                status: error?.response?.status || 0,
                detail,
                request_id: requestId,
            });
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "controller_status", level: "error",
                message: `Controller session upload failed; status ${error?.response?.status || 0}; ` +
                    `${payload.sample_count} samples; detail ${detail}`,
                status: error?.response?.status || 0,
                capture_session_id: captureSessionIdRef.current,
                attempt_count: UPLOAD_RETRY_DELAYS_MS.length,
            }}));
            toast.error("Controller analysis could not be saved.");
            return false;
        }
    };

    const finalizeSessionRecording = () => {
        if (recordingFinalizeRef.current) return recordingFinalizeRef.current;
        setSaving(true);
        recordingFinalizeRef.current = (async () => {
            await flushPendingClips();
            return saveFullSession();
        })().finally(() => {
            stopTracks();
            setSaving(false);
            recordingFinalizeRef.current = null;
        });
        return recordingFinalizeRef.current;
    };

    const stopCapture = async () => {
        if (stoppingRef.current) return;
        stoppingRef.current = true;
        setCapturing(false);
        capturingRef.current = false;
        setTwitchActive(false);
        stopFrameLoop();
        const controllerSummary = controllerResearchRef.current?.stop();
        const controllerSave = saveControllerSummary(controllerSummary);
        const rec = recorderRef.current;
        const hasChunks = chunksRef.current.length > 0;
        const hadRecording = Boolean((rec && rec.state !== "inactive") || hasChunks);
        // Finalize the match before the large recording begins consuming the
        // origin connection. The previous concurrent stop + upload sequence
        // made a transient upload reset capable of losing match finalization.
        try {
            if (rec && rec.state === "recording") {
                rec.requestData?.();
                rec.pause?.();
            }
        } catch { /* stopping below still flushes the recorder */ }
        let sessionResult;
        try {
            sessionResult = await putJsonWithRetry(
                "/session",
                { monitoring: false },
                {
                    requestId: `${captureSessionIdRef.current}:session-stop`,
                    onAttempt: ({ requestId, attempt, status, httpStatus }) => {
                        if (status !== "failed" && !(status === "completed" && attempt > 1)) return;
                        window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                            kind: "upload_attempt",
                            level: status === "failed" ? "warning" : "info",
                            message: `Session finalization ${requestId}; attempt ${attempt}; ${status}; HTTP ${httpStatus || 0}`,
                            status: httpStatus || 0,
                            attempt_count: attempt,
                            capture_session_id: captureSessionIdRef.current,
                        }}));
                    },
                },
            );
        } catch (error) {
            sessionResult = { error };
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "capture_status",
                level: "error",
                message: `Match finalization failed after retries; status ${error?.response?.status || 0}`,
                status: error?.response?.status || 0,
                attempt_count: UPLOAD_RETRY_DELAYS_MS.length,
                capture_session_id: captureSessionIdRef.current,
            }}));
        }
        let recordingSave = Promise.resolve(false);
        if (rec && rec.state !== "inactive") {
            recordingSave = new Promise((resolve) => {
                let finished = false;
                const finish = () => {
                    if (finished) return;
                    finished = true;
                    if (stopTimeoutRef.current) window.clearTimeout(stopTimeoutRef.current);
                    stopTimeoutRef.current = null;
                    resolve(finalizeSessionRecording());
                };
                rec.addEventListener("stop", finish, { once: true });
                stopTimeoutRef.current = window.setTimeout(finish, 5000);
                try {
                    rec.requestData?.();
                    rec.stop();
                } catch {
                    finish();
                }
            }).then((result) => result);
        } else if (hasChunks) {
            // Chrome may mark MediaRecorder inactive before the display-track ended handler runs.
            recordingSave = finalizeSessionRecording();
        } else {
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "upload_status", level: "error", message: "Monitoring stopped without recorded media chunks",
            }}));
            stopTracks();
        }
        if (videoRef.current) { videoRef.current.srcObject = null; videoRef.current.removeAttribute("src"); videoRef.current.load?.(); }
        toast(hadRecording ? "Saving full session recording…" : "Monitoring stopped.");
        try {
            const [controllerResult, recordingResult] = await Promise.allSettled([controllerSave, recordingSave]);
            const controllerSaved = controllerResult.status === "fulfilled" && controllerResult.value === true;
            const recordingSaved = recordingResult.status === "fulfilled" && recordingResult.value === true;
            const matchStatus = sessionResult?.error
                ? "request_failed"
                : sessionResult?.data?.match_finalization || "unknown";
            const finalizedMatch = sessionResult?.error ? null : sessionResult?.data?.finalized_match;
            window.dispatchEvent(new CustomEvent("ww:telemetry", { detail: {
                kind: "capture_status", level: controllerSaved && recordingSaved ? "info" : "error",
                message: `Capture stopped; controller_saved ${controllerSaved}; recording_saved ${recordingSaved}; ` +
                    `match_status ${matchStatus}; duration ${elapsedRef.current}s`,
                capture_session_id: captureSessionIdRef.current,
            }}));
            window.dispatchEvent(new CustomEvent("ww:session", { detail: {
                monitoring: false, ai_status: "idle", controller_saved: controllerSaved,
                recording_saved: recordingSaved, match_status: matchStatus,
                capture_session_id: captureSessionIdRef.current,
            }}));
            if (finalizedMatch) {
                toast.success(`Match saved: ${finalizedMatch.result} · ${finalizedMatch.kills}/${finalizedMatch.deaths}`);
            } else if (matchStatus === "rejected") {
                toast.error("Match stats were rejected as unreliable and were not added to Matches.");
            }
        } finally {
            stoppingRef.current = false;
        }
    };

    const retryFullSession = async () => {
        if (saving) return;
        if (!chunksRef.current.length) {
            await resumeStoredRecordings({ manual: true });
            return;
        }
        setSaving(true);
        try { await saveFullSession(); }
        finally { setSaving(false); }
    };

    const persist = (patch) => { api.put("/settings", patch).catch(() => {}); };

    const saveClip = async () => {
        if (!capturing || chunksRef.current.length === 0) {
            toast.error("Nothing buffered yet. Start Screen Capture first.");
            return;
        }
        setSaving(true);
        try {
            const blob = new Blob(chunksRef.current, { type: "video/webm" });
            const fd = new FormData();
            fd.append("file", blob, `clip-${Date.now()}.webm`);
            const qs = `?title=${encodeURIComponent(`${event} clip`)}&event_type=${encodeURIComponent(event)}&game=${game}`;
            await api.post(`/clips/upload${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Clip saved (buffer + event + after captured).");
        } catch {
            toast.error("Failed to save clip.");
        } finally { setSaving(false); }
    };

    const uploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const qs = `?title=${encodeURIComponent(file.name)}&event_type=${encodeURIComponent(event)}&game=${game}`;
            await api.post(`/clips/upload${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Video uploaded for AI analysis.");
        } catch {
            toast.error("Upload failed.");
        } finally { setSaving(false); e.target.value = ""; }
    };

    const changeSource = (id) => {
        if (capturing) return;
        setSource(id);
        setTwitchActive(false);
        persist({ stream_source: id });
    };

    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
    const twitchParent = typeof window !== "undefined" ? window.location.hostname : "localhost";

    return (
        <div className="ww-fade-up">
            <PageHeader title="Live Capture" subtitle="Real-time tactical AI — fast vision spotter with enemy callouts, silent in menus." />

            <DesktopBanner />

            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs" data-testid="controller-research-status">
                <span className={`h-2 w-2 rounded-full ${controllerStatus.connected ? "bg-success" : "bg-muted-foreground"}`} />
                <span className="text-muted-foreground">
                    Controller Research: {controllerStatus.connected ? "collecting passive input telemetry" : capturing ? "waiting for controller input" : "starts automatically with monitoring"}
                </span>
                {controllerStatus.connected && <span className="font-mono text-volt">{controllerStatus.samples} samples</span>}
                {controllerStatus.connected && controllerStatus.samplingGaps > 0 && (
                    <span className="font-mono text-warning">{controllerStatus.samplingGaps} sampling gaps</span>
                )}
                {controllerStatus.connected && controllerStatus.schedulerPauses > 0 && (
                    <span className="font-mono text-warning">{controllerStatus.schedulerPauses} scheduling pauses</span>
                )}
                {capturing && !controllerStatus.connected && (
                    <span className="text-warning">Press any controller button once while this tab is visible, then return to the game.</span>
                )}
            </div>

            {!hasPro && (
                <div className="mb-6 rounded-sm border border-volt/40 bg-gradient-to-r from-volt/[0.1] to-transparent p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="livecapture-paywall">
                    <Lock className="w-5 h-5 text-volt shrink-0" />
                    <p className="text-sm text-muted-foreground flex-1">
                        <span className="text-white font-semibold">Live AI Tactical Coach is a Pro feature.</span> Start a 7-day free trial to unlock real-time enemy callouts & coaching.
                    </p>
                    <Link to="/pricing" data-testid="livecapture-upgrade"
                        className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 shrink-0">
                        <Sparkles className="w-4 h-4" /> Start Free Trial
                    </Link>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    {/* Source selector */}
                    <div className="bg-surface border border-border rounded-sm p-4">
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Capture Source</label>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                            {SOURCES.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <button key={s.id} onClick={() => changeSource(s.id)} disabled={capturing} data-testid={`source-${s.id}`}
                                        className={cn("flex flex-col items-start gap-1 rounded-sm border px-3 py-2.5 text-left transition-colors disabled:opacity-50",
                                            source === s.id ? "border-volt bg-volt/10" : "border-border hover:border-white/25")}>
                                        <span className="flex items-center gap-2">
                                            <Icon className={cn("w-4 h-4", source === s.id ? "text-volt" : "text-muted-foreground")} />
                                            <span className={cn("text-sm font-semibold", source === s.id ? "text-volt" : "text-white")}>{s.label}</span>
                                        </span>
                                        <span className="text-[10px] text-muted-foreground">{s.desc}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {source === "stream" && (
                            <div className="mt-3 flex gap-2">
                                <input value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} disabled={capturing}
                                    placeholder="https://example.com/stream.m3u8" data-testid="stream-url-input"
                                    className="flex-1 h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white" />
                            </div>
                        )}
                        {source === "twitch" && (
                            <div className="mt-3 flex gap-2">
                                <input value={twitchChannel} onChange={(e) => setTwitchChannel(e.target.value)}
                                    placeholder="your_twitch_channel" data-testid="twitch-channel-input"
                                    className="flex-1 h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white" />
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    <div className="bg-surface border border-border rounded-sm p-4">
                        <div className="relative aspect-video bg-obsidian border border-border rounded-sm overflow-hidden ww-grid-bg">
                            {source === "twitch" && twitchActive ? (
                                <iframe
                                    title="Twitch Player" data-testid="twitch-embed"
                                    src={`https://player.twitch.tv/?channel=${twitchChannel}&parent=${twitchParent}&muted=false`}
                                    className="w-full h-full" allowFullScreen frameBorder="0" />
                            ) : (
                                <video ref={videoRef} autoPlay muted={source !== "stream"} playsInline controls={source === "stream"}
                                    className="w-full h-full object-contain" data-testid="capture-preview" />
                            )}

                            {!capturing && !twitchActive && (
                                <div className="absolute inset-0 grid place-items-center text-center px-6 pointer-events-none">
                                    <div>
                                        <MonitorPlay className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">
                                            {source === "screen" && "No active capture. Start screen recording to monitor your gameplay."}
                                            {source === "stream" && "Enter an HLS/m3u8 stream URL, then Start Monitoring."}
                                            {source === "twitch" && "Enter your Twitch channel, then Load Stream."}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {capturing && (
                                <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 rounded-sm bg-black/70 border border-alert/40">
                                    <span className="w-2.5 h-2.5 rounded-full bg-alert ww-live-dot" />
                                    <span className="font-mono text-xs text-white">REC {fmt(elapsed)}</span>
                                </div>
                            )}
                            {capturing && coach.loading && (
                                <div className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1 rounded-sm bg-black/70 border border-volt/40" data-testid="ai-analyzing-badge">
                                    <Loader2 className="w-3.5 h-3.5 text-volt animate-spin" />
                                    <span className="font-mono text-xs text-volt">AI ANALYZING</span>
                                </div>
                            )}
                            {capturing && captureBlackWarning && (
                                <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 rounded-sm border border-alert/60 bg-black/90 px-3 py-2 text-sm text-alert"
                                    data-testid="capture-black-warning">
                                    <AlertTriangle className="h-4 w-4 shrink-0" />
                                    Black capture detected. Stop and re-share the entire display before continuing.
                                </div>
                            )}
                            {capturing && latencySafeguard && !captureBlackWarning && (
                                <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 rounded-sm border border-warning/60 bg-black/90 px-3 py-2 text-sm text-warning"
                                    data-testid="capture-latency-safeguard">
                                    <AlertTriangle className="h-4 w-4 shrink-0" />
                                    Latency safeguard active: reduced AI frame load and capture frame rate.
                                </div>
                            )}
                        </div>

                        {/* Notices */}
                        {source === "twitch" && (
                            <div className="mt-3 flex items-start gap-2 rounded-sm border border-warn/40 bg-warn/[0.07] px-3 py-2.5" data-testid="twitch-notice">
                                <AlertTriangle className="w-4 h-4 text-warn mt-0.5 shrink-0" />
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    Browsers can't let the AI read frames from an embedded Twitch player. To get real AI coaching on a
                                    console game: keep your stream playing in a browser window, switch the source to <span className="text-white font-semibold">Screen Capture</span>,
                                    and select that window/tab — the AI will then watch it live.
                                </p>
                            </div>
                        )}
                        {source === "stream" && streamBlocked && (
                            <div className="mt-3 flex items-start gap-2 rounded-sm border border-warn/40 bg-warn/[0.07] px-3 py-2.5" data-testid="stream-blocked-notice">
                                <AlertTriangle className="w-4 h-4 text-warn mt-0.5 shrink-0" />
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    This stream blocks frame reading (CORS). Open it in a browser window and use <span className="text-white font-semibold">Screen Capture</span> for AI coaching.
                                </p>
                            </div>
                        )}

                        {/* Controls */}
                        <div className="flex flex-wrap gap-3 mt-4">
                            {!capturing ? (
                                <button data-testid="start-capture" onClick={startCapture} className="h-11 px-6 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2">
                                    {source === "twitch" ? <><Tv className="w-4 h-4" /> Load Stream</> : <><Radio className="w-4 h-4" /> Start Monitoring</>}
                                </button>
                            ) : (
                                <button data-testid="stop-capture" onClick={stopCapture} className="h-11 px-6 rounded-sm bg-alert text-white font-semibold hover:bg-[#ff4d7a] flex items-center gap-2">
                                    <Square className="w-4 h-4" /> Stop
                                </button>
                            )}
                            {source === "screen" && (
                                <button data-testid="save-clip" onClick={saveClip} disabled={!capturing || saving}
                                    className="h-11 px-6 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center gap-2 disabled:opacity-50">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Clip (Hotkey)
                                </button>
                            )}
                            {sessionUploadStatus === "failed" && (
                                <button data-testid="retry-session-upload" onClick={retryFullSession} disabled={saving}
                                    className="h-11 px-6 rounded-sm border border-alert/60 text-alert hover:bg-alert/10 font-semibold flex items-center gap-2 disabled:opacity-50">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Retry Session Upload
                                </button>
                            )}
                            {sessionUploadStatus === "blocked" && (
                                <div data-testid="capture-health-upload-blocked"
                                    className="w-full rounded-sm border border-alert/50 bg-alert/10 px-3 py-2 text-sm text-alert">
                                    Upload blocked because every sampled frame was black. Start a new capture, share the entire display, and verify the preview first.
                                </div>
                            )}
                            {["persisting", "uploading", "recovering", "saved"].includes(sessionUploadStatus) && (
                                <div data-testid="session-upload-safety"
                                    className={`w-full rounded-sm border px-3 py-2 text-sm ${sessionUploadStatus === "saved" ? "border-success/50 bg-success/10 text-success" : "border-warning/50 bg-warning/10 text-warning"}`}>
                                    {sessionUploadStatus === "saved"
                                        ? "Recording accepted by the server. Safe to close or restart."
                                        : sessionUploadStatus === "recovering"
                                            ? "Recovering an interrupted recording. Keep this page open until accepted."
                                            : "Recording is being preserved and uploaded. Keep this page open until it is safe to close."}
                                </div>
                            )}
                            <button data-testid="upload-video-btn" onClick={() => fileRef.current?.click()} disabled={saving}
                                className="h-11 px-6 rounded-sm border border-border hover:bg-white/5 font-semibold flex items-center gap-2 disabled:opacity-50">
                                <Upload className="w-4 h-4" /> Upload Video
                            </button>
                            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={uploadFile} data-testid="upload-video-input" />
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <LiveCoachPanel games={games} />
                    <div className="bg-surface border border-border rounded-sm p-5 space-y-4">
                        <h3 className="font-heading font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-volt" /> Capture Settings</h3>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Game</label>
                            <Select value={game} onValueChange={setGame}>
                                <SelectTrigger className="bg-obsidian border-border rounded-sm h-10 mt-1.5" data-testid="capture-game"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border">
                                    {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Rolling Buffer</label>
                            <div className="grid grid-cols-4 gap-2 mt-1.5">
                                {BUFFERS.map((b) => (
                                    <button key={b} onClick={() => setBuffer(b)} data-testid={`buffer-${b}`}
                                        className={`h-9 rounded-sm border font-mono text-sm transition-colors ${buffer === b ? "border-volt text-volt bg-volt/10" : "border-border text-muted-foreground hover:text-white"}`}>
                                        {b}s
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Save As Event</label>
                            <Select value={event} onValueChange={setEvent}>
                                <SelectTrigger className="bg-obsidian border-border rounded-sm h-10 mt-1.5" data-testid="capture-event"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border">
                                    {EVENTS.map((ev) => <SelectItem key={ev} value={ev}>{ev}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="bg-surface border border-border rounded-sm p-5">
                        <h3 className="font-heading font-bold flex items-center gap-2 mb-2"><Video className="w-4 h-4 text-volt" /> How it works</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            WhiteWater AI acts as your tactical operator — a fast vision spotter that calls out enemies by
                            clock position ("Enemy, 2 o'clock"), coaches you mid-fight, and auto-saves highlight clips. It
                            speeds up during combat and stays silent in lobbies, loadouts and death screens. Real gameplay
                            only — no scripted demo cues.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
